import type { Agent, GameEvent, GameMap, Player, Round } from "../api/types";
import { calloutAt, formatDuration, sideColor } from "../lib/view";
import { useCoarseTime } from "../player/store";

interface Props {
  events: GameEvent[];
  players: Player[];
  agents: Map<string, Agent>;
  round: Round;
  map: GameMap;
}

/** Kills and spike milestones that have already happened, most recent first. */
export function KillFeed({ events, players, agents, round, map }: Props) {
  const timeMs = useCoarseTime(200);

  const shown = events
    .filter((e) => e.timeMs <= timeMs && e.kind !== "damage")
    .slice(-12)
    .reverse();

  const name = (pawn: number | undefined) => {
    const player = players.find((p) => p.pawn === pawn);
    if (!player) return { label: "?", color: "#ffffff" };
    return {
      label: agents.get(player.codename)?.displayName ?? player.codename,
      color: sideColor(player, round),
    };
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col border-t border-white/10">
      <h3 className="px-3 pb-1 pt-3 text-[11px] uppercase tracking-wide opacity-50">
        Événements
      </h3>
      <ol className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 text-xs">
        {shown.length === 0 && <li className="opacity-40">Rien pour l'instant</li>}
        {shown.map((event, i) => {
          const at = event.x !== undefined && event.y !== undefined
            ? calloutAt(map, event.x, event.y)
            : "";
          const killer = name(event.actor);
          const victim = name(event.target);
          return (
            <li key={i} className="flex items-baseline gap-1.5 border-b border-white/5 py-1.5">
              <span className="w-9 shrink-0 font-mono opacity-40">
                {formatDuration(event.timeMs - round.startMs)}
              </span>
              <span className="min-w-0 flex-1">
                {event.kind === "kill" ? (
                  <>
                    <span style={{ color: killer.color }}>{killer.label}</span>
                    <span className="opacity-50"> élimine </span>
                    <span style={{ color: victim.color }}>{victim.label}</span>
                    {event.extra?.weapon && (
                      <span className="opacity-50"> · {event.extra.weapon}</span>
                    )}
                    {event.extra?.bone === "Head" && (
                      <span className="ml-1 rounded bg-amber-400/20 px-1 text-[10px] text-amber-300">
                        tête
                      </span>
                    )}
                  </>
                ) : event.kind === "plant" ? (
                  <span className="text-red-400">Spike posé{at ? ` — ${at}` : ""}</span>
                ) : event.kind === "defuse" ? (
                  <span className="text-cyan-300">Spike désamorcé</span>
                ) : event.kind === "detonate" ? (
                  <span className="text-orange-400">Spike explosé</span>
                ) : event.kind === "ultimate" ? (
                  <>
                    <span style={{ color: killer.color }}>{killer.label}</span>
                    <span className="opacity-50"> utilise son ultime</span>
                  </>
                ) : (
                  <span className="opacity-60">{event.kind}</span>
                )}
                {event.kind === "kill" && at && (
                  <span className="opacity-40"> · {at}</span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
